/**
 * iter703 — POS upsell strip.
 *
 * Horizontal row of 1-tap "Add fries · +5 SAR" chips that appears just
 * above the cart footer whenever the cart contains a trigger item with
 * matching upsell rules. Designed to:
 *   • stay under the cashier's thumb (no extra navigation)
 *   • disappear the instant the cart is empty
 *   • prevent re-prompting items already in the cart
 *
 * The fetch + filtering happens in `CashierPOSPage`; this component is
 * purely presentational so it stays cheap to render on every cart tick.
 */
import { Plus, Sparkles } from 'lucide-react';

export default function POSUpsellStrip({ suggestions, currencySymbol, onAdd }) {
  if (!Array.isArray(suggestions) || suggestions.length === 0) return null;
  return (
    <div className="border-t border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2.5" data-testid="pos-upsell-strip">
      <div className="flex items-center gap-1.5 mb-1.5 text-amber-900">
        <Sparkles size={12} className="text-amber-600" />
        <span className="text-[10px] uppercase tracking-wide font-bold">Suggested</span>
        {suggestions[0]?.triggered_by_name && (
          <span className="text-[10px] text-amber-700/80 truncate">· goes well with {suggestions[0].triggered_by_name}</span>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
        {suggestions.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onAdd(s)}
            className="snap-start flex-shrink-0 inline-flex items-center gap-2 bg-white border border-amber-200 rounded-xl px-2.5 py-1.5 shadow-sm hover:shadow-md hover:border-amber-300 active:scale-95 transition-all"
            data-testid={`pos-upsell-chip-${s.id}`}
          >
            <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700">
              <Plus size={14} />
            </span>
            <div className="text-left">
              <p className="text-[11px] font-semibold text-zinc-900 leading-tight max-w-[120px] truncate">{s.name}</p>
              {typeof s.price === 'number' && (
                <p className="text-[10px] text-amber-700">+ {currencySymbol} {s.price.toFixed(2)}</p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
